// src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ProcurementUserProvider } from './context/ProcurementUserContext';
import { AuthProvider } from './components/auth/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Account from './pages/Account';
import Knowledge from './pages/Knowledge';
import EdgeAgents from './pages/EdgeAgents';
import Assets from './pages/Assets';
import Events from './pages/Events';
import Policies from './pages/Policies';
import EdgeNodes from './pages/EdgeNodes';
import Chat from './pages/Chat';
import NetworkTopology from './pages/NetworkTopology';
import TopologyPage from './pages/TopologyPage';
import AISentryV2 from './pages/AISentryV2';
import AISentry from './pages/AISentry';
import AISentryMobile from './pages/AISentryMobile';
import FleetSentry from './pages/FleetSentry';
import DataCenterSentry from './pages/DataCenterSentry';
import DCSentry from './pages/DCSentry';
import DCFleet from './pages/DCFleet';
import PublicSafetySentry from './pages/PublicSafetySentry';
import PrinterOpsSentry from './pages/PrinterOpsSentry';
import PipelineSentry from './pages/PipelineSentry';
import BuildingSentry from './pages/BuildingSentry';
import ConstructionSentry from './pages/ConstructionSentry';
import OFEStory from './pages/OFEStory';
import EosaiStory from './pages/EosaiStory';
import OFEDashboard from './pages/ofe/OFEDashboard';
import OFEVendorDirectory from './pages/ofe/OFEVendorDirectory';
import OFEVendorDetail from './pages/ofe/OFEVendorDetail';
import OFEProductionPlanner from './pages/ofe/OFEProductionPlanner';
import OFEReallocationList from './pages/ofe/OFEReallocationList';
import OFEReallocationWorkspace from './pages/ofe/OFEReallocationWorkspace';
import OFEProjectDashboard from './pages/ofe/OFEProjectDashboard';
import OFEScheduleTracker from './pages/ofe/OFEScheduleTracker';
import OFEFinancialTracker from './pages/ofe/OFEFinancialTracker';
import OFEJEReview from './pages/ofe/OFEJEReview';
import OFEDocumentationTracker from './pages/ofe/OFEDocumentationTracker';
import OFEStorageLocations from './pages/ofe/OFEStorageLocations';
import OFEShipmentTracker from './pages/ofe/OFEShipmentTracker';
import OFECxLookahead from './pages/ofe/OFECxLookahead';
import OFECxIssues from './pages/ofe/OFECxIssues';
import OFEPrograms from './pages/ofe/OFEPrograms';
import MaritimeSentry from './pages/MaritimeSentry';
import ModularDCSentry from './pages/ModularDCSentry';
import HPExecutiveDashboard from './pages/HPExecutiveDashboard';
import HPRevenueControlSentry from './pages/HPRevenueControlSentry';
import SovereignEdgeTopology, { SovereignEdgePrint, SovereignEdgeMedical, SovereignEdgeDC, SovereignEdgeSubstation } from './pages/SovereignEdgeTopology';
import SovereignEdgeTopologyLight from './pages/SovereignEdgeTopologyLight';
import NetworkTopologyLight from './pages/NetworkTopologyLight';
import EventTraceLight from './pages/EventTraceLight';
import AISentryLight from './pages/AISentryLight';
import SignalsDashboard from './pages/SignalsDashboard';
import SignalsRawData from './pages/SignalsRawData';
import AxiomPortfolio from './pages/AxiomPortfolio';
import AxiomPortfolioV2 from './pages/AxiomPortfolioV2';
import AxiomPortfolioV3 from './pages/AxiomPortfolioV3';
import AxiomPolicies from './pages/AxiomPolicies';
import AxiomPoliciesV3 from './pages/AxiomPoliciesV3';
import AxiomPortfolioBriefing from './pages/AxiomPortfolioBriefing';
import AxiomNarrativeMicrosite from './pages/AxiomNarrativeMicrosite';
import PlatformStory from './pages/PlatformStory';
import LandManagementStory from './pages/landmanagmentstory';
import LandManagementStoryModern from './pages/landmanagementstory_modern';
import LandIntelligence from './pages/LandIntelligence';
import LandPipeline from './pages/LandPipeline';
import ParcelDetail from './pages/ParcelDetail';
import LandAlerts from './pages/LandAlerts';
import LandMap from './pages/LandMap';
import LandFilter from './pages/LandFilter';
import LandAHP from './pages/LandAHP';
import LandStageRules from './pages/LandStageRules';
import LandCalibration from './pages/LandCalibration';
import AxiomInteractiveProductTour from './pages/AxiomInteractiveProductTour';
import PrinterStory from './pages/PrinterStory';
import MedicalStory from './pages/MedicalStory';
import MedicalStoryInvestors from './pages/MedicalStoryInvestors';
import Axiomui from './pages/Axiomui';
import AxiomStakeholder from './pages/AxiomStakeholder';
import ProjectOracle from './pages/ProjectOracle';
import ProjectOracleV2 from './pages/ProjectOracleV2';
import IntelligenceDataSources from './pages/IntelligenceDataSources';
import IntelligenceAudit from './pages/IntelligenceAudit';
import IntelligenceMarkets from './pages/IntelligenceMarkets';
import Briefings from './pages/Briefings';
import FourRivers from './pages/FourRivers';
import ProcurementStory from './pages/ProcurementStory';
import ProcurementStoryV2 from './pages/ProcurementStory_v2';
import ProcurementStoryMobile from './pages/ProcurementStoryMobile';
import ProcurementStoryV2Mobile from './pages/ProcurementStory_v2_Mobile';
import PlatformStoryMobile from './pages/PlatformStoryMobile';
import ProcurementDashboard from './pages/ProcurementDashboard';
import ProductionPlanner from './pages/ProductionPlanner';
import ProjectFinancials from './pages/ProjectFinancials';
import DesignManagement from './pages/DesignManagement';
import ProjectSchedule from './pages/ProjectSchedule';
import ProjectDocumentation from './pages/ProjectDocumentation';
import CxLookAhead from './pages/CxLookAhead';
import SystemHealth from './pages/SystemHealth';
import EasterStory from './pages/easterstory';
import AlignedSalesWall from './pages/AlignedSalesWall';
import AlignedSalesWallMobile from './pages/AlignedSalesWallMobile';
import CodingTerminal from './pages/CodingTerminal';
import BusinessPlanner from './pages/BusinessPlanner';
import AutonomyTrace from './pages/AutonomyTrace';
import AdversarySimulation from './pages/AdversarySimulation';
import CESiteOverview from './pages/CESiteOverview';
import ComplianceCoverage from './pages/ComplianceCoverage';
import ComplianceInputs from './pages/ComplianceInputs';
import ComplianceScopeFacts from './pages/ComplianceScopeFacts';
import ComplianceRequirements from './pages/ComplianceRequirements';
import ComplianceRequirementDetail from './pages/ComplianceRequirementDetail';
import ComplianceReports from './pages/ComplianceReports';
import ComplianceFindings from './pages/ComplianceFindings';
import CEEvidenceExplorer from './pages/CEEvidenceExplorer';
import CETimelineViewer from './pages/CETimelineViewer';
import CEIncidentViewer from './pages/CEIncidentViewer';
import CEBundleExport from './pages/CEBundleExport';
import CEMyPackets from './pages/CEMyPackets';
import CEEvidencePacket from './pages/CEEvidencePacket';
import CEEvidenceReview from './pages/CEEvidenceReview';
import CEAttention from './pages/CEAttention';








function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected routes - auth check only */}
          <Route element={<ProtectedRoute />}>
            
            {/* Mobile routes - NO MainLayout (full screen mobile UI) */}
            <Route path="/mobile" element={<AISentryMobile />} />
            <Route path="/m" element={<AISentryMobile />} />
            <Route path="/m/procurement-story" element={<ProcurementStoryMobile />} />
            <Route path="/m/procurement-story-v2" element={<ProcurementStoryV2Mobile />} />
            <Route path="/m/platform-story" element={<PlatformStoryMobile />} />
            <Route path="/m/aligned" element={<AlignedSalesWallMobile />} />
            
            {/* Full-screen routes — no sidebar */}
            <Route path="/forge" element={<CodingTerminal />} />
            <Route path="/stratos" element={<BusinessPlanner />} />
            <Route path="/autonomy-trace" element={<AutonomyTrace />} />
            <Route path="/adversary-simulation" element={<AdversarySimulation />} />
            <Route path="/topology" element={<TopologyPage />} />
            <Route path="/event-trace-light" element={<EventTraceLight />} />
            <Route path="/ai-sentry" element={<AISentry />} />
            <Route path="/ai-sentry-light" element={<AISentryLight />} />

            {/* Desktop routes - WITH MainLayout (sidebar, header, etc) */}
            <Route element={<MainLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/account" element={<Account />} />
              <Route path="/knowledge" element={<Knowledge />} />
              <Route path="/edge-agents" element={<EdgeAgents />} />
              <Route path="/assets/*" element={<Assets />} />
              <Route path="/events/*" element={<Events />} />
              <Route path="/policies/*" element={<Policies />} />
              <Route path="/edge-nodes/*" element={<EdgeNodes />} />
              <Route path="/fleet-sentry" element={<FleetSentry />} />
              <Route path="/datacenter-sentry" element={<DataCenterSentry />} />
              <Route path="/safety-sentry" element={<PublicSafetySentry />} />
              <Route path="/printer-sentry" element={<PrinterOpsSentry />} />
              <Route path="/building-sentry" element={<BuildingSentry />} />
              <Route path="/construction-sentry" element={<ConstructionSentry />} />
              <Route path="/ofe-story" element={<OFEStory />} />
              <Route path="/eosai-story" element={<EosaiStory />} />
              {/* OFE fat skill — 18 pages */}
              <Route path="/ofe" element={<OFEDashboard />} />
              <Route path="/ofe/vendors" element={<OFEVendorDirectory />} />
              <Route path="/ofe/vendors/:id" element={<OFEVendorDetail />} />
              <Route path="/ofe/programs" element={<OFEPrograms />} />
              <Route path="/ofe/production/:vendorId" element={<OFEProductionPlanner />} />
              <Route path="/ofe/production/project/:projectId" element={<OFEProductionPlanner />} />
              <Route path="/ofe/reallocations" element={<OFEReallocationList />} />
              <Route path="/ofe/reallocations/:id" element={<OFEReallocationWorkspace />} />
              <Route path="/ofe/projects/:id" element={<OFEProjectDashboard />} />
              <Route path="/ofe/projects/:id/schedule" element={<OFEScheduleTracker />} />
              <Route path="/ofe/projects/:id/financials" element={<OFEFinancialTracker />} />
              <Route path="/ofe/projects/:id/financials/je-review" element={<OFEJEReview />} />
              <Route path="/ofe/projects/:id/docs" element={<OFEDocumentationTracker />} />
              <Route path="/ofe/financials" element={<OFEFinancialTracker />} />
              <Route path="/ofe/financials/je" element={<OFEJEReview />} />
              <Route path="/ofe/storage" element={<OFEStorageLocations />} />
              <Route path="/ofe/shipments" element={<OFEShipmentTracker />} />
              <Route path="/ofe/cx" element={<OFECxLookahead />} />
              <Route path="/ofe/cx/issues" element={<OFECxIssues />} />
              <Route path="/hp-executive" element={<HPExecutiveDashboard />} />
	      <Route path="/hp-revenue-control" element={<HPRevenueControlSentry />} />
              <Route path="/maritime-sentry" element={<MaritimeSentry />} />
              <Route path="/dc-sentry" element={<DCSentry />} />
              <Route path="/dc-fleet" element={<DCFleet />} />
              <Route path="/modular-dc-sentry" element={<ModularDCSentry />} />
              <Route path="/sovereign-edge" element={<SovereignEdgePrint />} />
              <Route path="/sovereign-edge-medical" element={<SovereignEdgeMedical />} />
              <Route path="/sovereign-edge-dc" element={<SovereignEdgeDC />} />
              <Route path="/sovereign-edge-substation" element={<SovereignEdgeSubstation />} />
              <Route path="/sovereign-edge-light" element={<SovereignEdgeTopologyLight />} />
	      <Route path="/topology-light" element={<NetworkTopologyLight />} />


              <Route path="/axiom-portfolio" element={<AxiomPortfolio />} />
              <Route path="/axiom-portfolio-v2" element={<AxiomPortfolioV2 />} />
              <Route path="/axiom-portfolio-v3" element={<AxiomPortfolioV3 />} />
              <Route path="/axiom-policies" element={<AxiomPolicies />} />
              <Route path="/axiom-policies-v3" element={<AxiomPoliciesV3 />} />
              <Route path="/axiom-briefing" element={<AxiomPortfolioBriefing />} />
              <Route path="/axiom-story" element={<AxiomNarrativeMicrosite />} />
              <Route path="/axiom-tour" element={<AxiomInteractiveProductTour />} />
              <Route path="/platform-story" element={<PlatformStory />} />
              <Route path="/land-management-story" element={<LandManagementStory />} />
              <Route path="/land-management-story-modern" element={<LandManagementStoryModern />} />
              <Route path="/land-intelligence" element={<LandIntelligence />} />
              <Route path="/land-intelligence/pipeline" element={<LandPipeline />} />
              <Route path="/land-intelligence/parcel/:parcelId" element={<ParcelDetail />} />
              <Route path="/land-intelligence/alerts" element={<LandAlerts />} />
              <Route path="/land-intelligence/map" element={<LandMap />} />
              <Route path="/land-intelligence/filter" element={<LandFilter />} />
              <Route path="/land-intelligence/ahp" element={<LandAHP />} />
              <Route path="/land-intelligence/stage-rules" element={<LandStageRules />} />
              <Route path="/land-intelligence/calibration" element={<LandCalibration />} />
              <Route path="/printer-story" element={<PrinterStory />} />
              <Route path="/medical-story" element={<MedicalStory />} />
              <Route path="/medical-story-investors" element={<MedicalStoryInvestors />} />
              <Route path="/axiom-intelligence" element={<Axiomui />} />
              <Route path="/axiom-stakeholder" element={<AxiomStakeholder />} />
              <Route path="/project-oracle" element={<ProjectOracle />} />
              <Route path="/project-oracle-v2" element={<ProjectOracleV2 />} />
              <Route path="/pipeline-sentry" element={<PipelineSentry />} />
              <Route path="/ai-sentry-v2" element={<AISentryV2 />} />
              <Route path="/signals" element={<SignalsDashboard />} />
              <Route path="/signals/raw" element={<SignalsRawData />} />
              <Route path="/intelligence/sources" element={<IntelligenceDataSources />} />
              <Route path="/intelligence/audit" element={<IntelligenceAudit />} />
              <Route path="/intelligence/markets" element={<IntelligenceMarkets />} />
              <Route path="/briefings" element={<Briefings />} />
              {/* Compliance Evidence skill — 6 pages */}
              <Route path="/compliance/coverage" element={<ComplianceCoverage />} />
              <Route path="/compliance/requirements" element={<ComplianceRequirements />} />
              <Route path="/compliance/requirements/:requirementId" element={<ComplianceRequirementDetail />} />
              <Route path="/compliance/inputs" element={<ComplianceInputs />} />
              <Route path="/compliance/scope-facts" element={<ComplianceScopeFacts />} />
              <Route path="/compliance/findings" element={<ComplianceFindings />} />
              <Route path="/compliance/reports" element={<ComplianceReports />} />
              <Route path="/ce" element={<CESiteOverview />} />
              <Route path="/ce/explorer" element={<CEEvidenceExplorer />} />
              <Route path="/ce/timelines" element={<CETimelineViewer />} />
              <Route path="/ce/incidents" element={<CEIncidentViewer />} />
              {/* "My Packets" — the customer surface. The PATH deliberately stays /ce/bundles so
                  existing bookmarks and deep links keep resolving (see CENavigation.test.jsx). */}
              <Route path="/ce/bundles" element={<CEMyPackets />} />
              {/* The original low-level tool: manual assembly, load-by-ID, stub artifacts. Removed
                  from the customer surface, NOT deleted — this is the QA/engineering path to
                  MANUAL_ASSEMBLY and null-class records that My Packets hides. */}
              <Route path="/ce/bundle-export" element={<CEBundleExport />} />
              <Route path="/ce/evidence-packet" element={<CEEvidencePacket />} />
            <Route path="/ce/review" element={<CEEvidenceReview />} />
            <Route path="/ce/attention" element={<CEAttention />} />
              <Route path="/four-rivers" element={<FourRivers />} />
              <Route path="/procurement-story" element={<ProcurementStory />} />
              <Route path="/procurement-story-v2" element={<ProcurementStoryV2 />} />
              <Route path="/procurement" element={<ProcurementUserProvider><Outlet /></ProcurementUserProvider>}>
                <Route index element={<ProcurementDashboard />} />
                <Route path="planner" element={<ProductionPlanner />} />
                <Route path="financials" element={<ProjectFinancials />} />
                <Route path="design" element={<DesignManagement />} />
                <Route path="schedule" element={<ProjectSchedule />} />
                <Route path="documentation" element={<ProjectDocumentation />} />
                <Route path="cx-lookahead" element={<CxLookAhead />} />
              </Route>
              <Route path="/health" element={<SystemHealth />} />
              <Route path="/easter-story" element={<EasterStory />} />
              <Route path="/aligned" element={<AlignedSalesWall />} />
              <Route path="/chat" element={<Chat />} />
            </Route>
          </Route>
          
          {/* Redirect root to Network Topology */}
          <Route path="/" element={<Navigate to="/health" replace />} />
          
          {/* 404 page */}
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900">404</h1>
                <p className="mt-2 text-lg text-gray-600">Page not found</p>
                <p className="mt-4">
                  <a href="/topology" className="text-blue-600 hover:text-blue-500">
                    Go back to Network Topology
                  </a>
                </p>
              </div>
            </div>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
